import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import SEOHead from '../components/SEOHead';
import { toolCategories } from '../data/toolRelations';
import { getCategoryNavItems } from '../data/categoryPageData';
import { 
  Search, ArrowRight, CheckCircle2, Zap, Shield, Globe,
  Type, Braces, Code2, Minimize2, BarChart3, Lock, Palette,
  Trophy, Sparkles, Repeat
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  'Text & Content Tools': Type,
  'Developer Tools': Braces,
  'Encoding & Decoding': Code2,
  'Minification Tools': Minimize2,
  'SEO & Web Tools': BarChart3,
  'Security Tools': Lock,
  'Design Tools': Palette,
};

const AllToolsPage = () => {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const categoryNavItems = getCategoryNavItems();

  const categoryIconMap = {
    type: Type,
    code: Code2,
    search: Search,
    sparkles: Sparkles,
    repeat: Repeat,
  };

  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response = await axios.get(`${API}/tools`);
        setTools(response.data);
      } catch (error) {
        console.error('Error fetching tools:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTools();
  }, []);

  const getToolBySlug = (slug) => tools.find(t => t.slug === slug);

  const filteredCategories = Object.entries(toolCategories).filter(([category, slugs]) => {
    if (!search) return true;
    return slugs.some(slug => {
      const tool = getToolBySlug(slug);
      return tool?.name.toLowerCase().includes(search.toLowerCase()) ||
             tool?.short_description.toLowerCase().includes(search.toLowerCase());
    });
  });

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ToolsForge",
    "url": window.location.origin,
    "description": "Free online tools for developers, designers, and marketers"
  };

  // ItemList Schema for tools
  const toolListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Free Online Tools",
    "description": "Complete list of free online tools for developers, designers, and marketers",
    "numberOfItems": tools.length,
    "itemListElement": tools.map((tool, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "SoftwareApplication",
        "name": tool.name,
        "description": tool.short_description,
        "url": `${window.location.origin}/tools/${tool.slug}`,
        "applicationCategory": "WebApplication",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        }
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="All Free Online Tools - 180+ Developer, SEO & Productivity Tools | ToolsForge"
        description="Browse all 180+ free online tools at ToolsForge. JSON formatter, password generator, Base64 encoder, URL encoder, HTML minifier, crypto calculators and more. No signup required, 100% free."
        keywords="free online tools, developer tools, SEO tools, productivity tools, JSON formatter, password generator, Base64 encoder, URL encoder, HTML minifier, code tools"
        canonical={`${window.location.origin}/all-tools`}
      />

      {/* Inject Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(toolListSchema) }} />

      <div className="min-h-screen pt-24 pb-16 px-6" data-testid="all-tools-page">
        <div className="max-w-6xl mx-auto">
          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <Badge variant="outline" className="mb-4 px-4 py-1.5 border-primary/30 text-primary">
              <Zap className="w-3 h-3 mr-2" />
              {tools.length > 0 ? tools.length : 180}+ Free Tools
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4 font-['Plus_Jakarta_Sans']">
              All Free Online Tools
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              The complete collection of free online tools for developers, designers, marketers, and content creators. 
              No signup required, no limits, 100% free forever.
            </p>

            {/* Search */}
            <div className="max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search all tools..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 bg-muted/50 border-border"
                  data-testid="search-all-tools"
                />
              </div>
            </div>
          </motion.div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 border border-border">
              <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
              <div>
                <div className="font-medium">100% Free</div>
                <div className="text-sm text-muted-foreground">No signup, no credit card</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 border border-border">
              <Shield className="w-6 h-6 text-primary flex-shrink-0" />
              <div>
                <div className="font-medium">Privacy First</div>
                <div className="text-sm text-muted-foreground">Data never leaves your browser</div>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 border border-border">
              <Globe className="w-6 h-6 text-accent flex-shrink-0" />
              <div>
                <div className="font-medium">Works Everywhere</div>
                <div className="text-sm text-muted-foreground">Desktop, tablet, mobile</div>
              </div>
            </div>
          </div>

          {/* Category Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mb-12"
          >
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans']">Browse by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {categoryNavItems.map((cat) => {
                const Icon = categoryIconMap[cat.icon] || Code2;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all text-center"
                    data-testid={`category-link-${cat.slug}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <span className="text-sm font-medium">{cat.name}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>

          {/* Best Tools Comparisons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="mb-12 p-6 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20"
          >
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-amber-500" />
              <h2 className="text-lg font-bold font-['Plus_Jakarta_Sans']">Best Tools Guides</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/compare/best-free-online-tools"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border hover:border-amber-500/50 transition-colors"
              >
                <Trophy className="w-4 h-4 text-amber-500" />
                <span className="text-sm">Best Free Online Tools 2026</span>
              </Link>
              <Link
                to="/compare/best-developer-tools"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border hover:border-amber-500/50 transition-colors"
              >
                <Code2 className="w-4 h-4 text-amber-500" />
                <span className="text-sm">Best Developer Tools</span>
              </Link>
              <Link
                to="/compare/best-seo-tools"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border border-border hover:border-amber-500/50 transition-colors"
              >
                <Search className="w-4 h-4 text-amber-500" />
                <span className="text-sm">Best SEO Tools</span>
              </Link>
            </div>
          </motion.div>

          {/* Tools by Category */}
          <div className="space-y-12">
            {filteredCategories.map(([category, slugs], categoryIndex) => {
              const CategoryIcon = categoryIcons[category] || Code2;
              const categoryTools = slugs.map(slug => getToolBySlug(slug)).filter(Boolean);
              
              if (search && categoryTools.length === 0) return null;
              
              const filteredTools = search 
                ? categoryTools.filter(tool => 
                    tool.name.toLowerCase().includes(search.toLowerCase()) ||
                    tool.short_description.toLowerCase().includes(search.toLowerCase())
                  )
                : categoryTools;

              if (filteredTools.length === 0) return null;

              return (
                <motion.section
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                  id={category.toLowerCase().replace(/\s+/g, '-')}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <CategoryIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">{category}</h2>
                      <p className="text-sm text-muted-foreground">{filteredTools.length} tools</p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTools.map((tool) => (
                      <Link
                        key={tool.slug}
                        to={`/tools/${tool.slug}`}
                        className="group p-4 rounded-xl border border-border bg-card hover:border-primary/50 hover:bg-card/80 transition-all"
                        data-testid={`tool-link-${tool.slug}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold group-hover:text-primary transition-colors">
                            {tool.name}
                          </h3>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              tool.tier === 'free' 
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' 
                                : 'bg-primary/10 text-primary border-primary/30'
                            }`}
                          >
                            {tool.tier}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                          {tool.short_description}
                        </p>
                        <div className="flex items-center text-primary text-sm">
                          Use Tool <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </motion.section>
              );
            })}
          </div>

          {/* Quick Navigation */}
          <div className="mt-16 p-6 rounded-xl bg-muted/30 border border-border">
            <h2 className="text-xl font-bold mb-4 font-['Plus_Jakarta_Sans']">Quick Navigation</h2>
            <div className="flex flex-wrap gap-2">
              {Object.keys(toolCategories).map((category) => (
                <a
                  key={category}
                  href={`#${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="px-3 py-1.5 rounded-full text-sm bg-background border border-border hover:border-primary/50 hover:text-primary transition-colors"
                >
                  {category}
                </a>
              ))}
            </div>
          </div>

          {/* SEO Content */}
          <div className="mt-16 prose prose-invert max-w-none">
            <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Why Use ToolsForge?</h2>
            <p className="text-muted-foreground">
              ToolsForge provides a comprehensive collection of free online tools designed for developers, 
              designers, marketers, and anyone who works with digital content. Our tools are built with 
              privacy in mind - all processing happens in your browser, meaning your data never leaves your device.
            </p>
            
            <h3 className="text-xl font-semibold mt-6">Popular Tools</h3>
            <ul className="text-muted-foreground space-y-2">
              <li><Link to="/tools/json-formatter" className="text-primary hover:underline">JSON Formatter</Link> - Format, validate, and beautify JSON data</li>
              <li><Link to="/tools/password-generator" className="text-primary hover:underline">Password Generator</Link> - Create secure, random passwords</li>
              <li><Link to="/tools/base64-encoder" className="text-primary hover:underline">Base64 Encoder</Link> - Encode and decode Base64 strings</li>
              <li><Link to="/tools/url-encoder" className="text-primary hover:underline">URL Encoder</Link> - Encode special characters for URLs</li>
              <li><Link to="/tools/html-minifier" className="text-primary hover:underline">HTML Minifier</Link> - Compress HTML for faster loading</li>
            </ul>

            <h3 className="text-xl font-semibold mt-6">For Developers</h3>
            <p className="text-muted-foreground">
              Our developer tools include <Link to="/tools/json-formatter" className="text-primary hover:underline">JSON formatting</Link>, 
              <Link to="/tools/regex-tester" className="text-primary hover:underline"> regex testing</Link>, 
              <Link to="/tools/timestamp-converter" className="text-primary hover:underline"> timestamp conversion</Link>, 
              <Link to="/tools/hash-generator" className="text-primary hover:underline"> hash generation</Link>, and more. 
              All tools are designed for quick, efficient workflows.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllToolsPage;
